import type {
  ModelPricingTier,
  ModelReviewEntry,
  ModelReviewField,
  ModelReviewPricingTier,
  ModelReviewSource,
  UpdateModelInput,
} from '../../../types';

/**
 * Pure helpers for the Model Catalog review tab (task 1093). The review
 * document (GET /models/review, service task 1092) carries, per Model row, the
 * stored values, the provider values and `differs[]`; these helpers read the
 * provider value for one field, decide which differences can be acted on, and
 * build the PATCH body that writes the provider value onto the Model row.
 */

export const FIELD_LABELS: Record<ModelReviewField, string> = {
  contextWindow: 'Context window',
  maxOutputTokens: 'Max output',
  inputPriceMTok: 'Input $/MTok',
  outputPriceMTok: 'Output $/MTok',
  cacheReadPriceMTok: 'Cache read $/MTok',
  cacheWritePriceMTok: 'Cache write $/MTok',
  pricingTiers: 'Pricing tiers',
};

/** Fields whose write changes what metering charges; they go through the pricing confirm. */
export const PRICE_FIELDS: ReadonlySet<ModelReviewField> = new Set<ModelReviewField>([
  'inputPriceMTok',
  'outputPriceMTok',
  'cacheReadPriceMTok',
  'cacheWritePriceMTok',
  'pricingTiers',
]);

export const LIMIT_FIELDS: ReadonlySet<ModelReviewField> = new Set<ModelReviewField>(['contextWindow', 'maxOutputTokens']);

export const SOURCE_LABELS: Record<string, string> = {
  anthropic: 'Anthropic',
  openai: 'OpenAI',
  'models-dev': 'models.dev',
};

export type ProviderValue = number | string | ModelReviewPricingTier[];

/**
 * The provider's value for one field of one Model row, or `undefined` when the
 * feed carries no value for it (task 1277). The review document writes `null`
 * for a value no source has (and an empty tier list means models.dev lists no
 * tiers); neither is a value to apply, because writing it would delete what
 * the catalog stores, as happened to the seeded long-context tiers.
 */
export function providerValueFor(entry: ModelReviewEntry, field: ModelReviewField): ProviderValue | undefined {
  const present = <T>(value: T | null | undefined): T | undefined => (value === null ? undefined : value);
  switch (field) {
    case 'contextWindow':
      return present(entry.provider_values.contextWindow);
    case 'maxOutputTokens':
      return present(entry.provider_values.maxOutputTokens);
    case 'inputPriceMTok':
      return present(entry.pricing?.base.inputPriceMTok);
    case 'outputPriceMTok':
      return present(entry.pricing?.base.outputPriceMTok);
    case 'cacheReadPriceMTok':
      return present(entry.pricing?.cache.readPriceMTok);
    case 'cacheWritePriceMTok':
      return present(entry.pricing?.cache.writePriceMTok);
    case 'pricingTiers': {
      const tiers = entry.pricing?.tiers;
      return Array.isArray(tiers) && tiers.length > 0 ? tiers : undefined;
    }
  }
}

/** Whether the feed carries a value for this field (task 1277); without one there is nothing to apply. */
export const providerHasValue = (entry: ModelReviewEntry, field: ModelReviewField): boolean =>
  providerValueFor(entry, field) !== undefined;

/** The stored value for one field of one Model row. */
export function storedValueFor(entry: ModelReviewEntry, field: ModelReviewField): number | string | ModelPricingTier[] | null {
  return entry.stored[field];
}

export function isModelsDevUnavailable(sources: ModelReviewSource[]): boolean {
  return sources.some((s) => s.id === 'models-dev' && s.status === 'unavailable');
}

/**
 * The differences on a row that get a "Use" action. A field the feed has no
 * value for gets none (task 1277). When models.dev is unavailable no
 * models.dev value is offered: every price field, and the limits of rows
 * whose limits come from models.dev (OpenAI).
 */
export function actionableDiffers(entry: ModelReviewEntry, modelsDevUnavailable: boolean): ModelReviewField[] {
  return entry.differs.filter((field) => {
    if (!providerHasValue(entry, field)) return false;
    if (!modelsDevUnavailable) return true;
    if (PRICE_FIELDS.has(field)) return false;
    return entry.provider_values.source !== 'models-dev';
  });
}

/**
 * PATCH body writing the provider value of each field onto the Model row. A
 * field the feed has no value for is left out, never written as null
 * (task 1277), so the patch can only set values, not clear them.
 */
export function patchFor(entry: ModelReviewEntry, fields: ModelReviewField[]): UpdateModelInput {
  const patch: UpdateModelInput = {};
  for (const field of fields) {
    const value = providerValueFor(entry, field);
    switch (field) {
      case 'contextWindow':
      case 'maxOutputTokens':
        if (typeof value === 'number') patch[field] = value;
        break;
      case 'inputPriceMTok':
      case 'outputPriceMTok':
      case 'cacheReadPriceMTok':
      case 'cacheWritePriceMTok':
        if (typeof value === 'string') patch[field] = value;
        break;
      case 'pricingTiers':
        if (Array.isArray(value)) patch.pricingTiers = value;
        break;
    }
  }
  return patch;
}

export const formatLimit = (value: number | null | undefined): string =>
  typeof value === 'number' ? value.toLocaleString('en-US') : '—';

/** A price as the catalog states it: "3.00", "0.30", "0.075". */
export function formatPrice(value: number | string | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—';
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return '—';
  return parsed.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 6 });
}

export function formatTiers(tiers: Array<{ over: number; inputPriceMTok: number | string; outputPriceMTok: number | string }> | null | undefined): string {
  if (!tiers || tiers.length === 0) return 'no tiers';
  return tiers
    .map((t) => `over ${t.over.toLocaleString('en-US')}: ${formatPrice(t.inputPriceMTok)} in / ${formatPrice(t.outputPriceMTok)} out`)
    .join('; ');
}

/** A field value for a confirm line: limits as counts, prices as dollars, tiers summarised. */
export function formatFieldValue(field: ModelReviewField, value: unknown): string {
  if (field === 'pricingTiers') return formatTiers(value as ModelPricingTier[] | null);
  if (LIMIT_FIELDS.has(field)) return formatLimit(value as number | null);
  return value === null || value === undefined ? '—' : `$${formatPrice(value as string)}`;
}

/** "just now", "4 min ago", "2 h ago", "3 d ago". */
export function formatCheckedAgo(iso: string | null | undefined, now: number = Date.now()): string {
  if (!iso) return 'never';
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return 'never';
  const minutes = Math.floor(Math.max(0, now - then) / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return `${hours} h ago`;
  return `${Math.floor(hours / 24)} d ago`;
}

/** YYYY-MM-DD for an ISO date or date-time; the input unchanged when it does not parse. */
export function formatDay(iso: string | null | undefined): string {
  if (!iso) return '—';
  const match = /^\d{4}-\d{2}-\d{2}/.exec(iso);
  return match ? match[0] : iso;
}
